// Cross-module validators + their TS types. The ONE vocabulary every module
// shares (schema.ts, function args, the SPA). Changing a literal set here is a
// contract change — note it at the bottom of play/DESIGN.md.
import { v, type Infer, type VLiteral, type VUnion } from 'convex/values';

/** A union of string literals: `lits('a', 'b')` ≡ `v.union(v.literal('a'), v.literal('b'))`. */
export function lits<const T extends string>(
  ...xs: [T, T, ...T[]]
): VUnion<T, VLiteral<T, 'required'>[], 'required', never> {
  const [a, b, ...rest] = xs.map((x) => v.literal(x));
  return v.union(a!, b!, ...rest) as unknown as VUnion<T, VLiteral<T, 'required'>[], 'required', never>;
}

// ---------------------------------------------------------------------------
// Geography
// ---------------------------------------------------------------------------
export const vLatLng = v.object({ lat: v.number(), lng: v.number() });
export type LatLng = Infer<typeof vLatLng>;

export const vPlaceKind = lits(
  'depot',
  'job_site',
  'supplier',
  'customer_office',
  'home',
  'fuel',
  'food',
  'nightlife',
  'mechanic',
  'hospital',
  'park',
  'landmark',
);
export type PlaceKind = Infer<typeof vPlaceKind>;

// ---------------------------------------------------------------------------
// Fleet
// ---------------------------------------------------------------------------
/** The three simulated providers. DEVICE RULE: a powered vehicle carries
 *  EITHER a VisionMax dashcam (which occupies the OBD-II port and reports
 *  vehicle_data) OR a Teltonika OBD-II tracker — never both. DigitalMatter
 *  battery trackers go only on non-powered assets. */
export const vProvider = lits('visionmax', 'teltonika', 'digitalmatter');
export type Provider = Infer<typeof vProvider>;

export const vVehicleKind = lits(
  'van',
  'bucket_truck',
  'box_truck',
  'pickup',
  'sedan',
  'generator_trailer',
  'cable_trailer',
  'tool_cage',
  'light_tower',
);
export type VehicleKind = Infer<typeof vVehicleKind>;
export const POWERED_KINDS: readonly VehicleKind[] = ['van', 'bucket_truck', 'box_truck', 'pickup', 'sedan'];

export const vVehicleState = lits(
  'parked', // engine off, not moving
  'idling', // engine on, stationary
  'driving',
  'towed', // a hitched non-powered asset moving behind its lead
  'crashed', // post-collision, immobile until recovered
  'in_service', // at the mechanic
);
export type VehicleState = Infer<typeof vVehicleState>;

/** Who is behind the wheel of a trip. */
export const vDriverRef = v.union(
  v.object({ kind: v.literal('persona'), personaId: v.id('personas') }),
  v.object({ kind: v.literal('human'), driveId: v.id('humanDrives') }),
  v.object({ kind: v.literal('none') }),
);
export type DriverRef = Infer<typeof vDriverRef>;

/** Per-trip driving style. Every field 0..1 except the booleans. The WORLD
 *  planner turns it into speed choice, following distance, harsh-event and
 *  collision hazard; TELEMETRY turns flags into provider events. */
export const vBehavior = v.object({
  aggression: v.number(), // harsh accel/brake/corner propensity
  speeding: v.number(), // how far over the limit they cruise (0 = at limit, 1 = +25 %)
  phoneUse: v.number(), // probability per minute of a phone-use episode
  distraction: v.number(), // eating, looking away, etc.
  fatigue: v.number(), // drowsy-event + reaction-time factor (rises at night / long shifts)
  seatbelt: v.number(), // probability the belt is ON
  rollingStops: v.number(),
  tailgating: v.number(),
});
export type Behavior = Infer<typeof vBehavior>;

export const vTripPurpose = lits(
  'job',
  'supply_run',
  'sales_visit',
  'estimate',
  'commute',
  'personal',
  'joyride',
  'fuel',
  'service',
  'return_to_depot',
  'owner_test',
);
export type TripPurpose = Infer<typeof vTripPurpose>;

export const vTripStatus = lits('planned', 'routing', 'active', 'completed', 'aborted');
export type TripStatus = Infer<typeof vTripStatus>;

/** Ground-truth sim events. TELEMETRY maps each kind to provider events
 *  (see DESIGN.md §"Provider event mapping"); AGENTS read them as world news. */
export const vIncidentKind = lits(
  'harsh_brake',
  'harsh_accel',
  'harsh_corner',
  'collision',
  'near_miss',
  'phone_use',
  'distracted',
  'drowsy',
  'tailgating',
  'no_seatbelt',
  'rolling_stop',
  'breakdown',
  'fault_code',
  'low_fuel',
  'tracker_unplugged',
  'after_hours_use',
  'asset_left_behind',
);
export type IncidentKind = Infer<typeof vIncidentKind>;

export const vSeverity = lits('low', 'medium', 'high', 'critical');
export type Severity = Infer<typeof vSeverity>;

export const vWeather = lits('clear', 'cloudy', 'rain', 'storm', 'fog', 'heat');
export type Weather = Infer<typeof vWeather>;

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------
export const vBreakpoint = lits('desktop', 'mobile');
export type Breakpoint = Infer<typeof vBreakpoint>;

/** Viewports the BROWSER module opens per breakpoint. */
export const VIEWPORTS: Record<Breakpoint, { width: number; height: number; mobile: boolean; deviceScaleFactor: number }> = {
  desktop: { width: 1440, height: 900, mobile: false, deviceScaleFactor: 1 },
  mobile: { width: 390, height: 844, mobile: true, deviceScaleFactor: 3 },
};

export const vPersonaRole = lits(
  'owner_ceo',
  'dispatcher_fleet_admin',
  'driver_electrician',
  'crew_lead',
  'bucket_operator',
  'office_clerk',
  'sales_ops_manager',
  'apprentice_runner',
);
export type PersonaRole = Infer<typeof vPersonaRole>;

/** The aa-app role a persona's seat should hold (formal app vocabulary). */
export const vAaAppRole = lits('owner', 'admin', 'manager', 'operator', 'viewer');
export type AaAppRole = Infer<typeof vAaAppRole>;

export const vAaAppStatus = lits('not_invited', 'invited', 'active', 'locked_out');

export const vActivity = lits(
  'asleep',
  'off_duty',
  'commuting',
  'working', // on site / in office
  'driving',
  'using_app',
  'in_meeting',
  'on_break',
);
export type Activity = Infer<typeof vActivity>;

export const vPersonaLocation = v.union(
  v.object({ kind: v.literal('place'), placeKey: v.string() }),
  v.object({ kind: v.literal('vehicle'), vehicleId: v.id('vehicles') }),
);

export const vSchedule = v.object({
  workdays: v.array(v.number()), // 0 = Sunday … 6 = Saturday (company tz)
  shiftStart: v.string(), // 'HH:MM' company-local
  shiftEnd: v.string(),
  sleepStart: v.string(),
  sleepEnd: v.string(),
  nightOwl: v.boolean(),
});
export type Schedule = Infer<typeof vSchedule>;

export const vAgentEventKind = lits(
  'wake',
  'gate', // a System-1 (Decider) verdict
  'thought',
  'decision',
  'action',
  'speech',
  'observation',
  'sleep',
  'error',
  'budget',
);
export type AgentEventKind = Infer<typeof vAgentEventKind>;

/** Optional cross-links on a feed event, for the spectator UI to deep-link. */
export const vEventRefs = v.object({
  wakeId: v.optional(v.id('personaWakes')),
  tripId: v.optional(v.id('trips')),
  taskId: v.optional(v.id('appTasks')),
  messageId: v.optional(v.id('messages')),
  emailId: v.optional(v.id('emails')),
  feedbackId: v.optional(v.id('feedbackReports')),
  incidentId: v.optional(v.id('incidents')),
  jobId: v.optional(v.id('jobs')),
  vehicleId: v.optional(v.id('vehicles')),
});

export const vFeedbackKind = lits('bug', 'ux', 'feature_request', 'praise', 'question');
export type FeedbackKind = Infer<typeof vFeedbackKind>;

export const vMessageAuthor = v.union(
  v.object({ kind: v.literal('persona'), personaId: v.id('personas') }),
  v.object({ kind: v.literal('owner') }),
  v.object({ kind: v.literal('system') }),
);

export const vEmailClassification = lits('invite', 'notification', 'magic_code', 'password_reset', 'other');

export const vJobKind = lits('service_call', 'install', 'inspection', 'emergency', 'supply_run', 'sales_visit', 'estimate');
export const vJobStatus = lits('open', 'assigned', 'en_route', 'on_site', 'done', 'late', 'cancelled');
export const vPriority = lits('low', 'normal', 'high', 'emergency');

// ---------------------------------------------------------------------------
// Browser tasks
// ---------------------------------------------------------------------------
export const vBrowserAction = v.object({
  type: lits('click', 'type', 'press', 'scroll', 'navigate', 'wait', 'back', 'select', 'hover', 'done', 'give_up'),
  /** Accessible target: `role "name"` from the aria snapshot, or a CSS selector prefixed `css=`. */
  target: v.optional(v.string()),
  text: v.optional(v.string()),
  x: v.optional(v.number()),
  y: v.optional(v.number()),
});
export type BrowserAction = Infer<typeof vBrowserAction>;

/** BROWSER fast path: how one operator step was decided ('fast' = System-1 / Decider, 'llm' = MiMo). */
export const vOperatorStepMode = lits('fast', 'llm');
/** The Decider's read of one step (present whenever it was consulted, fast or not). */
export const vOperatorStepDecider = v.object({
  /** `next` choice option id (r12 / scroll_down / back / wait / need_llm). */
  choice: v.string(),
  /** P(choice). */
  p: v.number(),
  /** P(goal achieved on this screen). */
  done: v.number(),
  /** P(stuck / looping / blocked). */
  stuck: v.number(),
  latencyMs: v.number(),
  /** A screenshot was attached to the Decider state. */
  image: v.optional(v.boolean()),
});
/** Per-task speed stats (fast path vs MiMo). Averages in ms; 0 when there were no such steps. */
export const vOperatorStats = v.object({
  fastSteps: v.number(),
  llmSteps: v.number(),
  /** Decider calls (fast steps + calls that deferred to MiMo). */
  deciderCalls: v.number(),
  /** Decider was consulted but the step went to MiMo (low confidence, need_llm, done/stuck, error). */
  deciderDeferred: v.number(),
  /** Fast actions that failed to execute. */
  fastFailures: v.number(),
  avgFastStepMs: v.number(),
  avgLlmStepMs: v.number(),
  avgDeciderMs: v.number(),
});
export type OperatorStats = Infer<typeof vOperatorStats>;

export const vAppTaskResult = v.object({
  summary: v.string(),
  success: v.boolean(),
  observations: v.array(v.string()),
  uxNotes: v.array(v.string()),
  bugs: v.array(v.object({ title: v.string(), detail: v.string(), route: v.optional(v.string()) })),
  finalUrl: v.optional(v.string()),
  /** The last step's screenshot (attach it to a feedback report). */
  screenshotStorageId: v.optional(v.id('_storage')),
  /** aa-app PostHog identity read from the sandbox page (links feedback to the session replay). */
  posthog: v.optional(v.object({ distinctId: v.optional(v.string()), sessionId: v.optional(v.string()) })),
  /** Operator speed stats (System-1 fast steps vs MiMo steps). */
  stats: v.optional(vOperatorStats),
});
export type AppTaskResult = Infer<typeof vAppTaskResult>;

export const vSpectatorRole = lits('viewer', 'admin');
export type SpectatorRole = Infer<typeof vSpectatorRole>;
